import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { apiClient } from "../../utils/http";
import { initials } from "../../utils/format";
import UserFormModal from "./UserFormModal";
import ResetPasswordModal from "./ResetPasswordModal";

/*
 * Store admin only: the store users who can open Job Orders, adding them and
 * resetting their passwords, plus the team's assignment switch.
 */
export default function UsersModal({ onClose, onSettingsChanged }) {
  const showToast = useToast();
  const { config, updateConfig } = useAuth();
  const assignmentEnabled = Boolean(config && config.assignment_enabled);
  const [users, setUsers] = useState(null);
  const [version, setVersion] = useState(0);
  const [form, setForm] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [savingSetting, setSavingSetting] = useState(false);

  const reload = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("users")
      .then((res) => !cancelled && setUsers(res.data.users))
      .catch((err) => {
        if (cancelled) return;
        setUsers((current) => current || []);
        showToast(err.message, "error");
      });
    return () => {
      cancelled = true;
    };
  }, [version, showToast]);

  const addUser = () => setForm({ user: null });

  const toggleAssignment = async () => {
    setSavingSetting(true);
    try {
      const res = await apiClient.post("settings", { assignment_enabled: !assignmentEnabled });
      updateConfig({ assignment_enabled: res.data.settings.assignment_enabled });
      showToast(res.data.message);
      if (onSettingsChanged) onSettingsChanged();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSavingSetting(false);
    }
  };

  return (
    <div className="modal-backdrop no-print">
      <div className="modal-container modal-md users-modal">
        <div className="modal-header">
          <h3>👥 Job Order Users</h3>
          <div className="users-header-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={addUser}>
              <svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
              Add User
            </button>
            <button type="button" className="btn-icon-close" title="Close" onClick={onClose}>
              &times;
            </button>
          </div>
        </div>
        <div className="modal-body">
          <div className="team-setting">
            <div className="team-setting-text">
              <strong id="assignment-setting-label">Task assignment</strong>
              <span>
                {assignmentEnabled
                  ? "On: bookings, meetings and tasks can be assigned to one or more users; each user sees only what they created or were assigned."
                  : "Off: every booking, meeting and task is shared with all users."}
              </span>
            </div>
            <label className="switch" title={assignmentEnabled ? "Turn assignment off" : "Turn assignment on"}>
              <input
                type="checkbox"
                role="switch"
                aria-labelledby="assignment-setting-label"
                checked={assignmentEnabled}
                disabled={savingSetting}
                onChange={toggleAssignment}
              />
              <span className="switch-track" />
            </label>
          </div>

          <p className="users-intro">Staff who can sign in to the Job Register Portal. New users get Job Order access only.</p>
          {users === null ? (
            <div className="app-loader" style={{ minHeight: 160 }}>
              <div className="spinner" />
            </div>
          ) : users.length === 0 ? (
            <div className="schedule-widget-empty" style={{ padding: "2.5rem 1rem" }}>
              <span>👥 No users added yet.</span>
              <button type="button" className="btn-widget-calendar" onClick={addUser}>
                + Add User
              </button>
            </div>
          ) : (
            <div className="users-list">
              {users.map((user) => (
                <div key={user.id} className={`user-row ${user.active ? "" : "inactive"}`}>
                  <div className="user-avatar">{initials(user.name)}</div>
                  <div className="user-row-info">
                    <div className="user-row-name">
                      <span>{user.name}</span>
                      <span className={`badge-status ${user.active ? "badge-completed" : "badge-onhold"}`}>{user.active ? "Active" : "Inactive"}</span>
                    </div>
                    <div className="contact-sub">
                      @{user.username} • {user.email}
                      {user.contact_number ? ` • ${user.contact_number}` : ""}
                    </div>
                  </div>
                  <div className="user-row-actions">
                    <button type="button" className="btn-table-action" onClick={() => setForm({ user })}>
                      ✏️ Edit
                    </button>
                    <button type="button" className="btn-table-action" onClick={() => setResetUser(user)}>
                      🔑 Reset Password
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {form && (
        <UserFormModal
          user={form.user}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            reload();
          }}
        />
      )}

      {resetUser && <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />}
    </div>
  );
}
